import React, {
  createContext,
  useCallback,
  useContext,
  useRef,
  useState,
} from "react";
import { useLocation } from "react-router-dom";
import { useDispatch } from "react-redux";
import { runDefaultRouteRefresh } from "../utils/defaultRouteRefresh";
import { ErrorToast } from "../utils/ShowToast";

const PageRefreshContext = createContext(null);

export const PageRefreshProvider = ({ children }) => {
  const location = useLocation();
  const dispatch = useDispatch();
  const handlersRef = useRef({});
  const [refreshing, setRefreshing] = useState(false);

  const register = useCallback((pathname, fn) => {
    handlersRef.current[pathname] = fn;
    return () => {
      if (handlersRef.current[pathname] === fn) {
        delete handlersRef.current[pathname];
      }
    };
  }, []);

  const refresh = useCallback(async () => {
    if (refreshing) return;

    const pathname = location.pathname;
    setRefreshing(true);
    try {
      const handler = handlersRef.current[pathname];
      if (handler) {
        await handler();
        return;
      }

      const handled = await runDefaultRouteRefresh({ pathname, dispatch });
      if (!handled) {
        window.dispatchEvent(
          new CustomEvent("icd:page-refresh", { detail: { pathname } })
        );
      }
    } catch {
      ErrorToast("Refresh failed");
    } finally {
      setRefreshing(false);
    }
  }, [dispatch, location.pathname, refreshing]);

  return (
    <PageRefreshContext.Provider value={{ register, refresh, refreshing }}>
      {children}
    </PageRefreshContext.Provider>
  );
};

export const usePageRefreshContext = () => useContext(PageRefreshContext);

export default PageRefreshContext;
